import React, { useRef, useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Divider,
} from "@mui/material";
import { Download, ArrowBack, Image as ImageIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
// @ts-ignore
import html2pdf from "html2pdf.js";
import { useSchools } from "../../hooks/useSchools";
import { useAuth } from "../../contexts/AuthContext";
import { FeeReceipt } from "../../services/paymentService";

interface FeeReceiptTemplateProps {
    receipt: FeeReceipt | null;
    onBack?: () => void;
}

const FeeReceiptTemplate: React.FC<FeeReceiptTemplateProps> = ({ receipt, onBack }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const schoolId = user?.schoolId;
    const { useSchoolById, useSchoolLogoInfo } = useSchools(false, { disableSchoolAdmins: true });
    const { data: schoolData, isLoading: schoolLoading } = useSchoolById(schoolId ?? undefined);
    const { data: logoInfo, isLoading: logoLoading } = useSchoolLogoInfo(schoolId ?? 1);

    const [isDownloadingImage, setIsDownloadingImage] = useState(false);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
    const templateRef = useRef<HTMLDivElement>(null);

    const schoolName = schoolData?.nameofSchool || "GOVT. SCHOOL";

    const numberToWords = (num: number): string => {
        const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const format = (n: number): string => {
            if (n < 20) return a[n];
            if (n < 100) return b[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + a[n % 10] : '');
            if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + format(n % 100) : '');
            if (n < 100000) {
                const thousands = Math.floor(n / 1000);
                return format(thousands) + ' Thousand' + (n % 1000 !== 0 ? ' ' + format(n % 1000) : '');
            }
            if (n < 10000000) {
                const lakhs = Math.floor(n / 100000);
                return format(lakhs) + ' Lakh' + (n % 100000 !== 0 ? ' ' + format(n % 100000) : '');
            }
            const crores = Math.floor(n / 10000000);
            return format(crores) + ' Crore' + (n % 10000000 !== 0 ? ' ' + format(n % 10000000) : '');
        };

        if (num === 0) return 'Zero';
        const result = format(Math.floor(num));
        return result.trim() + ' Only';
    };

    const formatDateTime = (date: any) => {
        if (!date) return "N/A";
        if (Array.isArray(date)) {
            const [year, month, day, hour, minute] = date;
            const ampm = (hour || 0) >= 12 ? 'PM' : 'AM';
            const displayHour = (hour || 0) % 12 || 12;
            const displayMinute = String(minute || 0).padStart(2, '0');
            return `${day}/${month}/${year} ${displayHour}:${displayMinute} ${ampm}`;
        }
        const d = new Date(date);
        const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
        const displayHour = d.getHours() % 12 || 12;
        const displayMinute = String(d.getMinutes()).padStart(2, '0');
        return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${displayHour}:${displayMinute} ${ampm}`;
    };

    const downloadAsImage = async () => {
        if (!templateRef.current) return;
        setIsDownloadingImage(true);
        try {
            const canvas = await html2canvas(templateRef.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: "#ffffff",
            } as any);
            const link = document.createElement("a");
            link.download = `receipt-${receipt?.paymentId || "print"}.png`;
            link.href = canvas.toDataURL("image/png");
            link.click();
        } catch (error) {
            console.error("Failed to download receipt:", error);
        } finally {
            setIsDownloadingImage(false);
        }
    };

    const downloadAsPdfFromImage = async () => {
        if (!templateRef.current) return;
        setIsDownloadingPdf(true);
        try {
            const el = templateRef.current;
            const opt = {
                margin: [4, 4, 4, 4],
                filename: `receipt-${receipt?.paymentId || "print"}.pdf`,
                image: { type: "jpeg", quality: 1.0 },
                html2canvas: {
                    scale: 2,
                    useCORS: true,
                    backgroundColor: "#ffffff",
                    logging: false,
                } as any,
                jsPDF: {
                    unit: "mm",
                    // Half of an A4 page (210 x 297mm) so the receipt only occupies
                    // the top half of a printed sheet, leaving the rest free.
                    // jsPDF enforces width<=height for "portrait", which would silently
                    // swap this array back to a near full-height page — use "landscape"
                    // (width>=height) so the 210x148.5 size is kept as-is.
                    format: [210, 148.5],
                    orientation: "landscape",
                    compress: false,
                    putOnlyUsedFonts: true,
                } as any,
                pagebreak: { mode: ["css", "avoid-all"] },
            };
            await html2pdf().set(opt).from(el).save();
        } catch (error) {
            console.error("Failed to download receipt:", error);
        } finally {
            setIsDownloadingPdf(false);
        }
    };

    if (schoolLoading || !receipt) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    const student = receipt.feeTransaction?.studentAccount?.student;
    const studentName = receipt.studentName || student?.studentName || "N/A";
    const scholarNo = student?.scholarNo || "N/A";
    const fatherName = student?.fatherName || "N/A";
    const mobileNo = student?.mobileNo || "N/A"

    // Use session and school class from the feeTransaction object as requested
    const transactionSession = receipt.feeTransaction?.session;
    const transactionClass = receipt.feeTransaction?.schoolClass;

    const sessionName = transactionSession?.name || student?.sessionName || student?.session?.name || receipt.sessionId || "N/A";
    const classBase = transactionClass?.name || student?.className || student?.schoolClass?.name || receipt.classId || "N/A";
    const section = transactionClass?.section || student?.section || student?.schoolClass?.section || "";
    const className = section ? `${classBase} ${section}` : classBase;

    const totalAmount = receipt.amount || receipt.feeTransaction?.amount || 0;
    const dueAmount = receipt.feeTransaction?.studentAccount?.dueAmount;
    const dateTime = formatDateTime(receipt.feeTransaction?.localDateTime);
    const payMode = receipt.feeTransaction?.paymentType || "CASH";
    const receiptNo = receipt.id || receipt.paymentId || "N/A";

    const tableStyle: React.CSSProperties = {
        width: "100%",
        borderCollapse: "collapse",
        marginTop: "6px",
        fontSize: "0.8rem",
    };

    const thStyle: React.CSSProperties = {
        border: "1px solid #000",
        padding: "4px 6px",
        backgroundColor: "#e0e0e0",
        textAlign: "left",
        fontWeight: "bold",
    };

    const tdStyle: React.CSSProperties = {
        border: "1px solid #000",
        padding: "4px 6px",
        textAlign: "left",
    };

    const headerCellStyle: React.CSSProperties = {
        padding: "2px 6px",
        fontSize: "0.8rem",
    };

    return (
        <Box sx={{ width: "100%", p: { xs: 1, sm: 2 } }}>
            {onBack && (
                <Box sx={{ display: "flex", mb: 2 }} className="no-print">
                    <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack}>
                        {t("back")}
                    </Button>
                </Box>
            )}

            <Box
                ref={templateRef}
                sx={{
                    bgcolor: "#fff",
                    p: 1.5,
                    width: "100%",
                    maxWidth: "800px",
                    margin: "0 auto",
                    border: "2px solid #000",
                    fontFamily: "'Times New Roman', Times, serif",
                    color: "#000",
                    boxSizing: "border-box",
                }}
            >
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1, gap: 1.5, px: 1, py: 0.5 }}>
                    {/* School Logo */}
                    <Box
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            border: "3px solid #fff",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                            overflow: "hidden",
                            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                        }}
                    >
                        {logoLoading ? (
                            <CircularProgress
                                size={24}
                                sx={{ color: "white" }}
                            />
                        ) : logoInfo && logoInfo.fileName ? (
                            <img
                                src={`${process.env.REACT_APP_API_URL}/schools/logos/${logoInfo.fileName}`}
                                alt={`${schoolName} Logo`}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    borderRadius: "50%",
                                }}
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = "none";
                                    const parent = target.parentElement;
                                    if (parent) {
                                        parent.innerHTML = `
                                            <div style="
                                                color: white; 
                                                font-weight: bold; 
                                                font-size: 14px; 
                                                text-align: center; 
                                                line-height: 1.2;
                                                display: flex;
                                                align-items: center;
                                                justify-content: center;
                                                width: 100%;
                                                height: 100%;
                                            ">
                                                ${schoolName.split(' ').map((word: string) => word.charAt(0)).join('').substring(0, 3)}
                                            </div>
                                        `;
                                    }
                                }}
                            />
                        ) : (
                            <Typography
                                variant="h6"
                                sx={{
                                    color: "white",
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                    textAlign: "center",
                                    lineHeight: 1.2,
                                }}
                            >
                                {schoolName.split(' ').map((word: string) => word.charAt(0)).join('').substring(0, 3)}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ flex: 1, textAlign: "center" }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ textTransform: "uppercase", mb: 0.3, fontSize: "18px", lineHeight: 1.2, color: "#000" }}>
                            {schoolName}
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: "12px", color: "#666" }}>
                            {schoolData?.blockName || ""}, {schoolData?.district || ""}, {schoolData?.state || ""} {schoolData?.pinNo || ""}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ border: "1px solid #000", bgcolor: "#e0e0e0", textAlign: "center", py: 0.3, mb: 1 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ letterSpacing: 2, fontSize: "15px" }}>
                        FEE RECEIPT
                    </Typography>
                </Box>

                {/* Receipt Info Grid */}
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: "1px solid #000", pb: 0.5, mb: 0.5 }}>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontWeight: "bold" }}>Receipt No</Typography>
                        <Typography sx={headerCellStyle}>: {receiptNo}</Typography>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontWeight: "bold" }}>Date & Time</Typography>
                        <Typography sx={headerCellStyle}>: {dateTime}</Typography>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontWeight: "bold" }}>Scholar No</Typography>
                        <Typography sx={headerCellStyle}>: {scholarNo}</Typography>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontWeight: "bold" }}>Session</Typography>
                        <Typography sx={headerCellStyle}>: {sessionName}</Typography>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontWeight: "bold" }}>Student Name</Typography>
                        <Typography sx={{ ...headerCellStyle, textTransform: "uppercase" }}>: {studentName}</Typography>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontWeight: "bold" }}>Class</Typography>
                        <Typography sx={headerCellStyle}>: {className}</Typography>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontWeight: "bold" }}>Father's Name</Typography>
                        <Typography sx={headerCellStyle}>: {fatherName}</Typography>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Typography sx={{ ...headerCellStyle, minWidth: "125px", fontWeight: "bold" }}>Mobile No.</Typography>
                        <Typography sx={headerCellStyle}>: {mobileNo}</Typography>
                    </Box>
                </Box>

                {/* Particulars Table */}
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, width: "50px" }}>Sl.No</th>
                            <th style={thStyle}>Particulars</th>
                            <th style={{ ...thStyle, width: "120px", textAlign: "right" }}>Paid</th>
                        </tr>
                    </thead>
                    <tbody>
                        {receipt.particulars && receipt.particulars.length > 0 ? (
                            receipt.particulars.map((p, idx) => (
                                <tr key={idx}>
                                    <td style={tdStyle}>{idx + 1}</td>
                                    <td style={tdStyle}>{t(p.description)}</td>
                                    <td style={{ ...tdStyle, textAlign: "right" }}>{p.amount.toLocaleString()}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td style={tdStyle}>1</td>
                                <td style={tdStyle}>Total Fees</td>
                                <td style={{ ...tdStyle, textAlign: "right" }}>{totalAmount.toLocaleString()}</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {/* Total Row */}
                <Box sx={{ bgcolor: "#e0e0e0", display: "flex", justifyContent: "space-between", px: 1, py: 0.3, border: "1px solid #000", borderTop: "none" }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.8rem" }}>Total</Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.8rem" }}>{totalAmount.toLocaleString()}</Typography>
                </Box>

                {/* Total in Words Row */}
                <Box sx={{ border: "1px solid #000", borderTop: "none", px: 1, py: 0.3 }}>
                    <Typography variant="body2" sx={{ fontStyle: "italic", fontSize: "0.75rem" }}>
                        Total in Words : {numberToWords(totalAmount)}
                    </Typography>
                </Box>

                {/* Due Amount Row */}
                {dueAmount != null && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", px: 1, py: 0.3, border: "1px solid #000", borderTop: "none" }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.8rem" }}>Due Amount</Typography>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.8rem" }}>
                            ₹{dueAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                    </Box>
                )}

                {/* Pay Mode Section */}
                <Box sx={{ border: "1px solid #000", borderTop: "none" }}>
                    <Box sx={{ bgcolor: "#e0e0e0", textAlign: "center", py: 0.2, borderBottom: "1px solid #000" }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.75rem" }}>PAY MODE INFORMATION</Typography>
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", p: 0.5 }}>
                        <Box sx={{ display: "flex" }}>
                            <Typography sx={{ ...headerCellStyle, minWidth: "110px", fontSize: "0.75rem", fontWeight: "bold", whiteSpace: "nowrap" }}>Pay Mode</Typography>
                            <Typography sx={{ ...headerCellStyle, fontSize: "0.75rem" }}>: {payMode}</Typography>
                        </Box>
                        <Box sx={{ display: "flex" }}>
                            <Typography sx={{ ...headerCellStyle, minWidth: "110px", fontSize: "0.75rem", fontWeight: "bold", whiteSpace: "nowrap" }}>Date & Time</Typography>
                            <Typography sx={{ ...headerCellStyle, fontSize: "0.75rem" }}>: {dateTime}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", gridColumn: "1 / -1" }}>
                            <Typography sx={{ ...headerCellStyle, minWidth: "110px", fontSize: "0.75rem", fontWeight: "bold", whiteSpace: "nowrap" }}>Transaction No</Typography>
                            <Typography sx={{ ...headerCellStyle, fontSize: "0.75rem" }}>: {receipt.feeTransaction?.transactionId || receipt.paymentId || "-"}</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3, flexWrap: "wrap" }} className="no-print">
                <Button variant="contained" startIcon={<ImageIcon />} onClick={downloadAsImage} disabled={isDownloadingImage}>
                    {isDownloadingImage ? t("downloading") : t("download_as_image")}
                </Button>
                <Button variant="contained" color="error" startIcon={<Download />} onClick={downloadAsPdfFromImage} disabled={isDownloadingPdf}>
                    {isDownloadingPdf ? t("downloading") : t("download_as_pdf")}
                </Button>
            </Box>

            <style>
                {`
                @media print {
                    .no-print {
                        display: none !important;
                    }
                    body {
                        background-color: white !important;
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                }
                `}
            </style>
        </Box>
    );
};

export default FeeReceiptTemplate;
